import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';
import { _t } from '../../utils/i18n';

// Définir le Badge comme un composant React à part entière avec une majuscule
const VariationsBadge = ({ measures }) => {

  const inconsistencyCount = useMemo(() => {
    if (!measures || !measures.length) return 0;

    // Logique simplifiée pour calculer le nombre d'incohérences
    const productStoreMap = new Map();

    measures.forEach(measure => {
      const key = `${measure.product}|${measure.interventionPoint}`;
      if (!productStoreMap.has(key)) {
        productStoreMap.set(key, {
          isPresent: measure.isPresent,
          hasChange: false
        });
      } else if (productStoreMap.get(key).isPresent !== measure.isPresent) {
        productStoreMap.get(key).hasChange = true;
      }
    });

    return Array.from(productStoreMap.values()).filter(item => item.hasChange).length;
  }, [measures]);

  return (
    <>
      {inconsistencyCount > 0 ? (
        <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 text-xs px-2 py-0.5 rounded-full min-w-[20px] text-center">
          {inconsistencyCount}
        </span>
      ) : (
        <span className="invisible text-xs px-2 py-0.5 min-w-[20px]">0</span>
      )}
    </>
  );
};

const DNControlVariations = ({ measures }) => {
  const [expandedProducts, setExpandedProducts] = useState([]);
  const [selectedReason, setSelectedReason] = useState(null);
  const selectStyles = useSelectStyles();

  // Analyse des incohérences: produits avec changements de statut dans le même magasin
  const inconsistencies = useMemo(() => {
    if (!measures.length) return [];

    // Pour chaque produit et magasin, regrouper les mesures par date
    const productStoreMap = new Map();

    measures.forEach(measure => {
      const productId = measure.product;
      const storeId = measure.interventionPoint;
      const key = `${productId}|${storeId}`;

      if (!productStoreMap.has(key)) {
        productStoreMap.set(key, {
          product: productId,
          store: storeId,
          shopSign: measure.shopSign,
          hasNotInStoreRange: measure.reasonCategory === 'Not in Store Range',
          measures: []
        });
      }

      // Mettre à jour le flag hasNotInStoreRange si nécessaire
      if (measure.reasonCategory === 'Not in Store Range') {
        productStoreMap.get(key).hasNotInStoreRange = true;
      }

      productStoreMap.get(key).measures.push({
        date: new Date(measure.date),
        isPresent: measure.isPresent,
        reason: measure.reason,
        reasonCategory: measure.reasonCategory,
        isNotInStoreRange: measure.reasonCategory === 'Not in Store Range',
        username: measure.username
      });
    });

    // Identifier les produits×magasins qui ont des changements de statut
    let inconsistentEntries = [];

    productStoreMap.forEach(entry => {
      // Trier les mesures par date
      entry.measures.sort((a, b) => a.date - b.date);

      // Vérifier s'il y a des changements de statut
      let hasStatusChange = false;
      for (let i = 1; i < entry.measures.length; i++) {
        if (entry.measures[i].isPresent !== entry.measures[i-1].isPresent) {
          hasStatusChange = true;
          break;
        }
      }

      if (hasStatusChange) {
        inconsistentEntries.push(entry);
      }
    });

    // Trier par produit (ordre alphabétique)
    return inconsistentEntries.sort((a, b) => a.product.localeCompare(b.product));
  }, [measures]);

  // Extraire toutes les raisons d'absence distinctes des incohérences
  const reasonOptions = useMemo(() => {
    const reasons = new Set();

    inconsistencies.forEach(entry => {
      entry.measures.forEach(measure => {
        if (!measure.isPresent && measure.reason) {
          reasons.add(measure.reason);
        }
      });
    });

    return Array.from(reasons).map(reason => ({
      value: reason,
      label: reason
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [inconsistencies]);

  // Fonction pour basculer l'état d'expansion d'un produit
  const toggleProductExpand = (productId) => {
    setExpandedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Grouper les incohérences par produit pour une meilleure organisation
  const groupedInconsistencies = useMemo(() => {
    const grouped = {};

    inconsistencies.forEach(entry => {
      if (!grouped[entry.product]) {
        grouped[entry.product] = [];
      }
      grouped[entry.product].push(entry);
    });

    return grouped;
  }, [inconsistencies]);

  // Compter les cas avec "Not in Store Range"
  const notInStoreRangeCount = useMemo(() => {
    return inconsistencies.filter(entry => entry.hasNotInStoreRange).length;
  }, [inconsistencies]);

  const filteredGroupedInconsistencies = useMemo(() => {
    if (!selectedReason) return groupedInconsistencies;
    return Object.fromEntries(
      Object.entries(groupedInconsistencies).map(([product, entries]) => [
        product,
        entries.filter(entry => entry.measures.some(measure => measure.reason === selectedReason.value))
      ]).filter(([_, entries]) => entries.length > 0)
    );
  }, [groupedInconsistencies, selectedReason]);

  if (inconsistencies.length === 0) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md">
        <p className="text-blue-700 dark:text-blue-300">
          {selectedReason
            ? `${_t('No inconsistency detected with the reason')} "${selectedReason.label}" ${_t('for the selected period.')}`
            : `${_t('No inconsistency detected in the data for the selected period.')}`
          }
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <Select
          options={reasonOptions}
          value={selectedReason}
          onChange={setSelectedReason}
          placeholder={_t("Filter by reason...")}
          isClearable
          styles={selectStyles}
          className="react-select-container"
          classNamePrefix="react-select"
        />
      </div>

      <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
        <p className="text-yellow-700 dark:text-yellow-300 text-sm flex items-center flex-col">
          {inconsistencies.length} situations où le statut d'un produit change dans le même magasin pendant la période sélectionnée.
          {notInStoreRangeCount > 0 && (
            <span className="ml-2 flex items-center text-orange-600 dark:text-orange-400">
              <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              dont {notInStoreRangeCount} avec statut "Not in Store Range" (à surveiller)
            </span>
          )}
        </p>
      </div>

      <div className="space-y-4 mb-6">
        {Object.entries(filteredGroupedInconsistencies).map(([product, entries]) => {
          const hasNotInStoreRange = entries.some(entry => entry.hasNotInStoreRange);

          return (
            <div key={product} className={`border rounded-lg overflow-hidden ${
              hasNotInStoreRange
                ? 'border-orange-300 dark:border-orange-700'
                : 'border-gray-200 dark:border-gray-700'
            }`}>
              <div
                className={`p-3 flex justify-between items-center cursor-pointer ${
                  hasNotInStoreRange
                    ? 'bg-orange-50 dark:bg-orange-900/20'
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}
                onClick={() => toggleProductExpand(product)}
              >
                <h3 className="font-medium text-gray-800 dark:text-white flex items-center">
                  {hasNotInStoreRange && (
                    <svg className="w-5 h-5 mr-2 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  {product}
                </h3>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                    {entries.length} magasin(s)
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transform ${expandedProducts.includes(product) ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {expandedProducts.includes(product) && (
                <div className="p-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Magasin
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Enseigne
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Changements de statut
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                      {entries.map((entry, idx) => (
                        <tr key={idx} className={entry.hasNotInStoreRange ? 'bg-orange-50/50 dark:bg-orange-900/10' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                            <div className="flex items-center">
                              {entry.hasNotInStoreRange && (
                                <svg className="w-5 h-5 mr-2 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                              )}
                              {entry.store}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                            {entry.shopSign}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                            <div className="space-y-2">
                              {entry.measures.map((measure, midx) => (
                                <div
                                  key={midx}
                                  className={`flex items-center p-2 rounded-md border ${
                                    measure.isPresent
                                      ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-900/20'
                                      : measure.isNotInStoreRange
                                        ? 'border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-900/20'
                                        : 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-900/20'
                                  }`}
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                      <span className="text-gray-600 dark:text-gray-400">
                                        {measure.date.toLocaleDateString()} {measure.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}:
                                      </span>
                                      {measure.username && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                                          par {measure.username}
                                        </span>
                                      )}
                                    </div>

                                    <div className="flex items-start mt-1">
                                      {measure.isPresent ? (
                                        <span className="text-green-600 dark:text-green-400 font-medium">Présent</span>
                                      ) : (
                                        <div className="flex flex-col">
                                          <div className="flex items-center">
                                            <span className={`font-medium ${
                                              measure.isNotInStoreRange
                                                ? 'text-orange-600 dark:text-orange-400'
                                                : 'text-red-600 dark:text-red-400'
                                            }`}>
                                              Absent
                                            </span>
                                          </div>
                                          {measure.reason && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                              Raison: {measure.reason}
                                              {measure.reasonCategory && (
                                                <span className={`${
                                                  measure.isNotInStoreRange
                                                    ? 'text-orange-600 dark:text-orange-400'
                                                    : ''
                                                }`}>
                                                  {' '}{measure.reasonCategory}
                                                </span>
                                              )}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {midx < entry.measures.length - 1 && (
                                    <div className="w-4 h-6 flex justify-center items-center mx-auto ml-2">
                                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

// Assigner le composant à DNControlVariations.Badge
DNControlVariations.Badge = VariationsBadge;

export default DNControlVariations;